import React, { useState } from 'react';
import { Typography, message, Tooltip, Badge } from 'antd';
import {
  UserOutlined,
  CodeOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  CONTACT_VARS,
  COMMON_SESSION_VARS,
  API_RESPONSE_PATHS,
  VAR_RULES,
} from '../constants/flowPlaceholdersReference';
import { buildPlaceholderTokens } from '../utils/flowVars';

const { Text } = Typography;

/* ─── Copy Chip ──────────────────────────────────────────────── */
const CopyChip = ({ token, color, bg, border }) => {
  const [flash, setFlash] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(token).then(() => {
      setFlash(true);
      message.success('Copied to clipboard!', 1);
      setTimeout(() => setFlash(false), 800);
    });
  };
  return (
    <Tooltip title="Click to copy" placement="top">
      <span
        onClick={copy}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px 3px 10px',
          borderRadius: 20,
          background: flash ? color : bg,
          color: flash ? '#fff' : color,
          fontFamily: 'monospace',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          border: `1.5px solid ${border || color + '40'}`,
          transition: 'all 0.18s ease',
          userSelect: 'none',
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        {token}
        <CopyOutlined style={{ fontSize: 10, opacity: 0.7 }} />
      </span>
    </Tooltip>
  );
};

/* ─── Section Header ─────────────────────────────────────────── */
const SectionHeader = ({ icon, title, count, gradient, textColor }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      borderRadius: 12,
      background: gradient,
      marginBottom: 14,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: 'rgba(255,255,255,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          color: textColor || '#fff',
        }}
      >
        {icon}
      </span>
      <Text strong style={{ color: textColor || '#fff', fontSize: 14 }}>
        {title}
      </Text>
    </div>
    {count !== undefined && (
      <span
        style={{
          background: 'rgba(255,255,255,0.3)',
          color: textColor || '#fff',
          borderRadius: 20,
          padding: '1px 10px',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {count}
      </span>
    )}
  </div>
);

/* ─── Variable Card ──────────────────────────────────────────── */
const VarCard = ({ token, color, bg, border, label, desc, sub }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 10,
      border: '1px solid #f0f0f0',
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'box-shadow 0.2s',
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)')
    }
  >
    <CopyChip token={token} color={color} bg={bg} border={border} />
    {label && (
      <Text strong style={{ fontSize: 12, color: '#374151' }}>
        {label}
      </Text>
    )}
    {desc && (
      <Text style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
        {desc}
      </Text>
    )}
    {sub && (
      <Text style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>
        {sub}
      </Text>
    )}
  </div>
);

/* ─── Stats Bar ──────────────────────────────────────────────── */
const StatsBadge = ({ count, label, color }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '10px 20px',
      background: '#fff',
      borderRadius: 10,
      border: '1px solid #f0f0f0',
      flex: 1,
    }}
  >
    <Text strong style={{ fontSize: 20, color, lineHeight: 1 }}>
      {count}
    </Text>
    <Text style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{label}</Text>
  </div>
);

/* ─── Main Component ─────────────────────────────────────────── */
const VariablesGuide = ({ definition }) => {
  const flowTokens = buildPlaceholderTokens(definition || {});
  const totalVars =
    flowTokens.length +
    CONTACT_VARS.length +
    COMMON_SESSION_VARS.length +
    API_RESPONSE_PATHS.length;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Stats Bar ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <StatsBadge count={flowTokens.length} label="This Flow" color="#7c3aed" />
        <StatsBadge count={CONTACT_VARS.length} label="Contact" color="#0ea5e9" />
        <StatsBadge count={COMMON_SESSION_VARS.length} label="Session" color="#10b981" />
        <StatsBadge count={API_RESPONSE_PATHS.length} label="API" color="#f59e0b" />
      </div>

      <Text
        type="secondary"
        style={{ fontSize: 11, display: 'block', marginBottom: 20, textAlign: 'center' }}
      >
        💡 Click any token to copy it — paste directly into message bodies, API URLs, or conditions
      </Text>

      {/* ── This Flow ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader
          icon={<ThunderboltOutlined />}
          title="This Flow"
          count={flowTokens.length}
          gradient="linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)"
        />
        {flowTokens.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {flowTokens.map((t) => (
              <CopyChip key={t} token={t} color="#7c3aed" bg="#f5f3ff" border="#c4b5fd" />
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: '16px',
              borderRadius: 10,
              background: '#faf5ff',
              border: '1.5px dashed #c4b5fd',
              textAlign: 'center',
            }}
          >
            <ThunderboltOutlined style={{ fontSize: 22, color: '#c4b5fd', marginBottom: 8 }} />
            <br />
            <Text style={{ fontSize: 12, color: '#9ca3af' }}>
              No session variables yet. Add{' '}
              <strong style={{ color: '#7c3aed' }}>Ask question</strong> or{' '}
              <strong style={{ color: '#7c3aed' }}>Set variable</strong> nodes and
              they'll appear here automatically.
            </Text>
          </div>
        )}
      </div>

      {/* ── Contact Vars ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader
          icon={<UserOutlined />}
          title="Contact — Always Available"
          count={CONTACT_VARS.length}
          gradient="linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)"
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {CONTACT_VARS.map((v) => (
            <VarCard
              key={v.token}
              token={v.token}
              color="#0369a1"
              bg="#e0f2fe"
              border="#7dd3fc"
              label={v.name}
              desc={v.description}
              sub={`e.g. ${v.example}  ·  ${v.usedIn.join(', ')}`}
            />
          ))}
        </div>
      </div>

      {/* ── Session Vars ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader
          icon={<CodeOutlined />}
          title="Session Variables"
          count={COMMON_SESSION_VARS.length}
          gradient="linear-gradient(135deg, #10b981 0%, #34d399 100%)"
        />
        <div
          style={{
            marginBottom: 10,
            padding: '8px 14px',
            background: '#f0fdf4',
            borderRadius: 8,
            border: '1px solid #bbf7d0',
            fontSize: 12,
            color: '#166534',
          }}
        >
          Format: <CopyChip token="{{vars.<name>}}" color="#166534" bg="#dcfce7" border="#86efac" />
          &nbsp;— the name comes from your Ask / Set variable / API nodes
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {COMMON_SESSION_VARS.map((v) => (
            <VarCard
              key={v.token}
              token={v.token}
              color="#065f46"
              bg="#d1fae5"
              border="#6ee7b7"
              label={v.name}
              desc={v.description}
              sub={`Set by: ${v.setBy}`}
            />
          ))}
        </div>
      </div>

      {/* ── API Response ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader
          icon={<ApiOutlined />}
          title="API Response — Conditions & Lists"
          count={API_RESPONSE_PATHS.length}
          gradient="linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)"
          textColor="#78350f"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {API_RESPONSE_PATHS.map((v) => (
            <VarCard
              key={v.path}
              token={v.token}
              color="#92400e"
              bg="#fef3c7"
              border="#fcd34d"
              desc={v.description}
              sub={`Used in: ${v.usedIn.join(', ')}`}
            />
          ))}
        </div>
      </div>

      {/* ── Rules ── */}
      <div>
        <SectionHeader
          icon={<InfoCircleOutlined />}
          title="Rules & Tips"
          gradient="linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {VAR_RULES.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 14px',
                background: '#fff',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
                fontSize: 12,
                color: '#4b5563',
                lineHeight: 1.6,
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#6b7280',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {i + 1}
              </span>
              {r}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VariablesGuide;
